/** VISTOERA_CANONICAL_NODE_REGISTRY_PROVIDER_INVENTORY_ICON_SYNC_AND_
 * QUICK_ACTION_MANAGEMENT_V1 — Platform Admin "快捷动作" config section
 * (spec §18/§20/§22).
 *
 * Renders the CODE-owned, capability-validated legal Quick Actions for
 * one Canvas source node type (`GET /platform/provider-options/quick-
 * actions?source_node_type=...`) and lets the admin toggle `enabled`
 * and reorder via `sort_order` swaps
 * (`PATCH /platform/provider-options/quick-actions/{action_key}`).
 *
 * Deliberately NOT a workflow editor (spec §14/§34): the admin can only
 * enable/disable/reorder/relabel actions the backend's
 * `NodeQuickActionDefinition` catalog already declares legal for this
 * source node type — `target_node_type`/`target_capability` are never
 * admin-editable here.
 *
 * The target icon shown per row is always the target node's own
 * canonical icon (`getNodeDefinition(target_node_type).icon`) — spec
 * §19 "Quick Action icon = target Node canonical icon" — so changing a
 * node's icon via `NodeIconPicker` automatically updates this panel
 * too, with no per-row icon override in V1.
 */
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons'
import { Button, Space, Switch, Typography, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { platformListQuickActions, platformSetQuickAction, type QuickActionAdminRow } from '../api'
import { getNodeDefinition } from '../workspace/contentProduction/canvas/nodeRegistry'
import { platformColors } from './theme'

interface Props {
  sourceNodeType: string | null
}

function errMsg(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { detail?: { message?: string } | string } }; message?: string }
  const detail = e?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object' && detail.message) return detail.message
  return e?.message || fallback
}

export default function NodeQuickActionsPanel({ sourceNodeType }: Props) {
  const [actions, setActions] = useState<QuickActionAdminRow[]>([])
  const [loading, setLoading] = useState(false)
  const [busyKey, setBusyKey] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!sourceNodeType) {
      setActions([])
      return
    }
    setLoading(true)
    try {
      const res = await platformListQuickActions(sourceNodeType)
      setActions((res.data.actions ?? []).slice().sort((a, b) => a.sort_order - b.sort_order))
    } catch {
      setActions([])
    } finally {
      setLoading(false)
    }
  }, [sourceNodeType])

  useEffect(() => {
    void load()
  }, [load])

  const toggle = async (row: QuickActionAdminRow, enabled: boolean) => {
    setBusyKey(row.action_key)
    try {
      const res = await platformSetQuickAction(row.action_key, { enabled })
      setActions((prev) => prev.map((r) => (r.action_key === row.action_key ? res.data : r)))
    } catch (err) {
      message.error(errMsg(err, '更新快捷动作失败'))
    } finally {
      setBusyKey(null)
    }
  }

  const move = async (row: QuickActionAdminRow, direction: -1 | 1) => {
    const idx = actions.findIndex((a) => a.action_key === row.action_key)
    const swapWith = idx + direction
    if (idx < 0 || swapWith < 0 || swapWith >= actions.length) return
    const a = actions[idx]
    const b = actions[swapWith]
    setBusyKey(a.action_key)
    try {
      const [resA, resB] = await Promise.all([
        platformSetQuickAction(a.action_key, { sort_order: b.sort_order }),
        platformSetQuickAction(b.action_key, { sort_order: a.sort_order }),
      ])
      setActions((prev) => {
        const next = prev.map((r) => {
          if (r.action_key === resA.data.action_key) return resA.data
          if (r.action_key === resB.data.action_key) return resB.data
          return r
        })
        return next.sort((x, y) => x.sort_order - y.sort_order)
      })
    } catch (err) {
      message.error(errMsg(err, '调整顺序失败'))
    } finally {
      setBusyKey(null)
    }
  }

  if (!sourceNodeType) return null

  return (
    <div data-testid="node-quick-actions-panel" style={{ marginBottom: 10 }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        快捷动作（Hover Quick Action）— 决定 Canvas 中该类资产 Hover
        时展示哪些后续动作；合法目标由代码保证，这里只控制启用/顺序。
      </Typography.Text>
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {loading && actions.length === 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            加载中…
          </Typography.Text>
        )}
        {!loading && actions.length === 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            该节点当前没有已定义的合法快捷动作。
          </Typography.Text>
        )}
        {actions.map((row, idx) => {
          const targetDef = getNodeDefinition(row.target_node_type)
          return (
            <div
              key={row.action_key}
              data-testid={`quick-action-row-${row.action_key}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 8,
                border: `1px solid ${platformColors.border}`,
                opacity: row.enabled ? 1 : 0.55,
              }}
            >
              <span style={{ color: targetDef.color, fontSize: 16, flexShrink: 0 }}>{targetDef.icon}</span>
              <span style={{ flex: 1, fontSize: 13, minWidth: 0 }}>{row.label}</span>
              <span style={{ fontSize: 11, color: platformColors.mutedText, flexShrink: 0 }}>{row.action_key}</span>
              <Space size={2}>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={idx === 0 || busyKey === row.action_key}
                  onClick={() => void move(row, -1)}
                />
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={idx === actions.length - 1 || busyKey === row.action_key}
                  onClick={() => void move(row, 1)}
                />
              </Space>
              <Switch
                size="small"
                checked={row.enabled}
                disabled={busyKey === row.action_key}
                onChange={(v) => void toggle(row, v)}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
